import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiServer, FiWifiOff } from "react-icons/fi";
import { API_BASE_URL } from "../../services/http";

// Ao voltar do bloqueio, a rede do aparelho leva 1-2 s para responder. Por isso
// o aviso só aparece depois de três falhas seguidas (RET-01).
const RETRY_DELAYS_MS = [1000, 3000];
const FAILURES_TO_SHOW = 3;
const DOWN_INTERVAL_MS = 5000;
const IDLE_INTERVAL_MS = 15000;
const RESUME_DELAY_MS = 1000;

type PingResult = "ok" | "fail" | "aborted";

function pingHealth(signal: AbortSignal): Promise<PingResult> {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/health`;
  return fetch(url, { cache: "no-store", signal })
    .then((r): PingResult => (r.ok ? "ok" : "fail"))
    .catch((): PingResult => (signal.aborted ? "aborted" : "fail"));
}

function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function isPageHidden() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export default function OfflineOverlay() {
  const [down, setDown] = useState(false);
  const [offline, setOffline] = useState(false);
  const [checking, setChecking] = useState(false);

  const failuresRef = useRef(0);
  const downRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  // Debug leve: logar base usada em dev
  if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
    // eslint-disable-next-line no-console
    console.debug("[OfflineOverlay] API_BASE_URL=", API_BASE_URL);
  }

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const check = useCallback(async () => {
    stop();
    const controller = new AbortController();
    controllerRef.current = controller;
    setChecking(true);
    const result = await pingHealth(controller.signal);
    // Uma checagem mais nova (ou a página oculta) assumiu: esta é descartada.
    if (!mountedRef.current || controllerRef.current !== controller) return;
    controllerRef.current = null;
    setChecking(false);
    setOffline(isBrowserOffline());

    // Requisição cancelada não conta como falha (RET-02).
    if (result === "aborted") return;

    let nextDelay: number;
    if (result === "ok") {
      failuresRef.current = 0;
      downRef.current = false;
      setDown(false);
      nextDelay = IDLE_INTERVAL_MS;
    } else {
      failuresRef.current += 1;
      if (downRef.current || failuresRef.current >= FAILURES_TO_SHOW) {
        downRef.current = true;
        setDown(true);
        nextDelay = DOWN_INTERVAL_MS;
      } else {
        nextDelay = RETRY_DELAYS_MS[failuresRef.current - 1];
      }
    }

    // Com a página oculta nada fica agendado; a volta agenda o próximo teste.
    if (!isPageHidden()) {
      timerRef.current = setTimeout(() => void check(), nextDelay);
    }
  }, [stop]);

  useEffect(() => {
    mountedRef.current = true;

    const scheduleResume = () => {
      stop();
      setChecking(false);
      // O aviso só some com um teste que passa; fora dele, a contagem recomeça.
      if (!downRef.current) failuresRef.current = 0;
      timerRef.current = setTimeout(() => void check(), RESUME_DELAY_MS);
    };

    const handleVisibility = () => {
      if (isPageHidden()) {
        stop();
        setChecking(false);
        return;
      }
      scheduleResume();
    };

    const handlePageShow = (event: Event) => {
      if ((event as PageTransitionEvent).persisted) scheduleResume();
    };

    const handleOnline = () => {
      setOffline(false);
      void check();
    };

    const handleOffline = () => setOffline(true);

    void check();
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      mountedRef.current = false;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [check, stop]);

  if (!down) return null;

  const Icon = offline ? FiWifiOff : FiServer;
  const title = offline ? "Sem conexão" : "Servidores indisponíveis";
  const description = offline
    ? "Confira sua internet. Vamos reconectar sozinhos quando ela voltar."
    : "Nossa API está temporariamente fora do ar. Já estamos trabalhando para restabelecer. Tente novamente em instantes.";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/95 p-4">
      <div
        role="alert"
        className="w-full max-w-sm rounded-3xl border border-slate-200/80 bg-white p-6 text-center shadow-subtle"
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-700">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h2 className="mb-1 text-lg font-semibold text-slate-900">{title}</h2>
        <p className="mb-5 text-sm text-slate-500">{description}</p>
        <button
          type="button"
          onClick={() => void check()}
          disabled={checking}
          className="h-11 w-full rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition duration-150 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:opacity-50"
        >
          {checking ? "Verificando..." : "Tentar novamente"}
        </button>
      </div>
    </div>
  );
}
